import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export interface TermsFieldsData {
  sponsorName: string;
  sponsorAddress: string;
  eligibleRegions: string;
  minimumAge: string;
  excludedStates: string; // New: states where void
  startDate: string;
  startTime: string;
  prizeDescription: string;
  prizeArv: string;
  prizeQuantity: string; // New: e.g., "One (1)"
  totalPrizeValue: string; // New: if multiple prizes
  winnerNotificationMethod: string;
  winnerNotificationTimeframe: string;
  responseWindow: string;
  winnerSelectionDate: string; // New: specific date or timeframe
  governingState: string;
  supportEmail: string;
  mailInAddress: string; // New: for mail-in entries
  entryLimit: string; // New: e.g., "one entry per person per day"
  includeMarketingOptIn: string; // New: "yes" or "no"
  [key: string]: string; // Index signature for Json compatibility
}

interface TermsFieldsAccordionProps {
  values: TermsFieldsData;
  onChange: (field: keyof TermsFieldsData, value: string) => void;
}

export function TermsFieldsAccordion({ values, onChange }: TermsFieldsAccordionProps) {
  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="terms-fields" className="border rounded-xl px-4">
        <AccordionTrigger className="hover:no-underline">
          <span className="font-heading font-bold">Terms & Conditions Fields</span>
        </AccordionTrigger>
        <AccordionContent className="space-y-4 pt-4">
          <p className="text-sm text-muted-foreground mb-4">
            These fields populate the Terms & Conditions template shown in the contest footer modal.
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sponsorName">Sponsor Name</Label>
              <Input
                id="sponsorName"
                value={values.sponsorName}
                onChange={(e) => onChange("sponsorName", e.target.value)}
                placeholder="Company Inc."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sponsorAddress">Sponsor Address</Label>
              <Input
                id="sponsorAddress"
                value={values.sponsorAddress}
                onChange={(e) => onChange("sponsorAddress", e.target.value)}
                placeholder="123 Main St, City, Country"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="eligibleRegions">Eligible Regions</Label>
              <Input
                id="eligibleRegions"
                value={values.eligibleRegions}
                onChange={(e) => onChange("eligibleRegions", e.target.value)}
                placeholder="United States, Canada"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="minimumAge">Minimum Age</Label>
              <Input
                id="minimumAge"
                value={values.minimumAge}
                onChange={(e) => onChange("minimumAge", e.target.value)}
                placeholder="18"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date (if no contest start date)</Label>
              <Input
                id="startDate"
                value={values.startDate}
                onChange={(e) => onChange("startDate", e.target.value)}
                placeholder="January 1, 2024"
              />
              <p className="text-xs text-muted-foreground">
                Uses contest start date if set, otherwise uses this value
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="startTime">Start Time</Label>
              <Input
                id="startTime"
                value={values.startTime}
                onChange={(e) => onChange("startTime", e.target.value)}
                placeholder="12:00 AM EST"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="prizeDescription">Prize Description</Label>
              <Input
                id="prizeDescription"
                value={values.prizeDescription}
                onChange={(e) => onChange("prizeDescription", e.target.value)}
                placeholder="One (1) iPhone 15 Pro"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prizeArv">Prize ARV (Approx. Retail Value)</Label>
              <Input
                id="prizeArv"
                value={values.prizeArv}
                onChange={(e) => onChange("prizeArv", e.target.value)}
                placeholder="$1,199 USD"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="winnerNotificationMethod">Winner Notification Method</Label>
              <Input
                id="winnerNotificationMethod"
                value={values.winnerNotificationMethod}
                onChange={(e) => onChange("winnerNotificationMethod", e.target.value)}
                placeholder="email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="winnerNotificationTimeframe">Notification Timeframe</Label>
              <Input
                id="winnerNotificationTimeframe"
                value={values.winnerNotificationTimeframe}
                onChange={(e) => onChange("winnerNotificationTimeframe", e.target.value)}
                placeholder="7 days"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="responseWindow">Response Window</Label>
              <Input
                id="responseWindow"
                value={values.responseWindow}
                onChange={(e) => onChange("responseWindow", e.target.value)}
                placeholder="48 hours"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="governingState">Governing State/Jurisdiction</Label>
              <Input
                id="governingState"
                value={values.governingState}
                onChange={(e) => onChange("governingState", e.target.value)}
                placeholder="California"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="supportEmail">Support Email</Label>
              <Input
                id="supportEmail"
                value={values.supportEmail}
                onChange={(e) => onChange("supportEmail", e.target.value)}
                placeholder="support@example.com"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="excludedStates">Excluded States (if any)</Label>
              <Input
                id="excludedStates"
                value={values.excludedStates}
                onChange={(e) => onChange("excludedStates", e.target.value)}
                placeholder="Florida, New York"
              />
              <p className="text-xs text-muted-foreground">
                States where promotion is void (leave blank if none)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prizeQuantity">Prize Quantity</Label>
              <Input
                id="prizeQuantity"
                value={values.prizeQuantity}
                onChange={(e) => onChange("prizeQuantity", e.target.value)}
                placeholder="One (1)"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="totalPrizeValue">Total Prize Value (if multiple)</Label>
              <Input
                id="totalPrizeValue"
                value={values.totalPrizeValue}
                onChange={(e) => onChange("totalPrizeValue", e.target.value)}
                placeholder="$5,000 USD"
              />
              <p className="text-xs text-muted-foreground">
                Only needed if awarding multiple prizes
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="winnerSelectionDate">Winner Selection Date</Label>
              <Input
                id="winnerSelectionDate"
                value={values.winnerSelectionDate}
                onChange={(e) => onChange("winnerSelectionDate", e.target.value)}
                placeholder="on or about January 15, 2024"
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to use notification timeframe
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mailInAddress">Mail-In Entry Address (optional)</Label>
            <Input
              id="mailInAddress"
              value={values.mailInAddress}
              onChange={(e) => onChange("mailInAddress", e.target.value)}
              placeholder="Attn: Contest Entry, 123 Main St, City, State ZIP"
            />
            <p className="text-xs text-muted-foreground">
              If provided, mail-in entry option will be included in terms
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="entryLimit">Entry Limit (optional)</Label>
            <Input
              id="entryLimit"
              value={values.entryLimit}
              onChange={(e) => onChange("entryLimit", e.target.value)}
              placeholder="one entry per person per day"
            />
            <p className="text-xs text-muted-foreground">
              Describe any entry frequency limits
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="includeMarketingOptIn">Include Marketing Opt-In Language?</Label>
            <select
              id="includeMarketingOptIn"
              value={values.includeMarketingOptIn}
              onChange={(e) => onChange("includeMarketingOptIn", e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Adds section about optional marketing communications
            </p>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}